"use client"

import { useState, useRef, useEffect } from "react"
import { read, utils } from "xlsx"
import "../css/ccrm.css"

const CATEGORY_LIST_URL = "/api/categories/list"
const ATTRIBUTE_LIST_URL = "/api/categories/attributes"

const MODE_ORDER = ["cuentas", "categorias", "productos", "boms", "empleados"]
const DEFAULT_MODE = MODE_ORDER[0]

const MODE_CONFIG = {
  cuentas: {
    label: "Cuentas",
    apiUrl: "/api/accounts/bulk",
    collectionKey: "accounts",
    heroTitle: "Carga de cuentas contables",
    fileNote: "Encabezados requeridos: Código, Nombre y Moneda.",
    summaryUnit: "cuenta(s)",
    footnote: "Consejo: revisa que el XLSX sea UTF-8 y contenga Código, Nombre y Moneda.",
    emptyMessage: "No se encontró ninguna fila completa con Código, Nombre y Moneda.",
    skipMessage: (n) => `Hay ${n} fila(s) con columnas obligatorias vacías.`,
  },
  categorias: {
    label: "Categorías",
    apiUrl: "/api/categories/bulk",
    collectionKey: "categories",
    heroTitle: "Carga de categorías",
    fileNote: "Encabezados requeridos: Categoría, Padre, Cuenta de ingresos, Cuenta de gasto y Cuenta de anticipo.",
    summaryUnit: "categoría(s)",
    footnote: "Consejo: valida que cada fila tenga las cuentas de ingresos/gasto/anticipo.",
    emptyMessage: "No se encontró ninguna fila completa con Categoría y las cuentas requeridas.",
    skipMessage: (n) => `Hay ${n} fila(s) con datos incompletos.`,
  },
  productos: {
    label: "Productos",
    apiUrl: "/api/products/bulk",
    collectionKey: "products",
    heroTitle: "Carga de productos",
    fileNote:
      "Encabezados requeridos: Producto, Referencia, Precio Venta, Coste, Cuenta de ingresos, Cuenta de gasto y Cuenta de diferencia de precio.",
    summaryUnit: "producto(s)",
    footnote: "Consejo: asigna una categoría y define atributos antes de enviar.",
    emptyMessage:
      "Completa Producto, Referencia, precios, cuentas y asigna categoría para al menos una fila.",
    skipMessage: (n) => `Hay ${n} fila(s) sin datos obligatorios o sin categoría asignada.`,
  },
  boms: {
    label: "Lista de materiales",
    apiUrl: "/api/boms/bulk",
    collectionKey: "boms",
    heroTitle: "Carga de listas de materiales (BOM)",
    fileNote:
      "Encabezados sugeridos: Referencia Producto, Referencia Variante, Referencia Receta, Cantidad, Componente, Cantidad (componente), Unidad de medida del producto.",
    summaryUnit: "lista(s)",
    footnote:
      "Consejo: puedes dejar celdas vacías para reutilizar la última referencia de producto/variante/receta.",
    emptyMessage: "Completa al menos una BOM con referencias y componentes.",
    skipMessage: (n) => `Hay ${n} fila(s) con datos incompletos. Revisa referencias y componentes.`,
  },
  empleados: {
    label: "Empleados",
    apiUrl: "/api/employees/bulk",
    collectionKey: "employees",
    heroTitle: "Carga de empleados",
    fileNote:
      "Encabezados sugeridos: Primer/Segundo Nombre, Primer/Segundo Apellido, correos, teléfonos, etiquetas, departamento, puesto, datos IGSS, dirección y contacto de emergencia.",
    summaryUnit: "empleado(s)",
    footnote: "Consejo: separa las etiquetas con comas y revisa los formatos de fecha.",
    emptyMessage: "Completa al menos un empleado con nombres y correo corporativo.",
    skipMessage: (n) => `Hay ${n} fila(s) con datos obligatorios faltantes.`,
  },
}

const ACCOUNT_ALIAS = {
  codigo: ["codigo", "código"],
  nombre: ["nombre"],
  moneda: ["moneda"],
}
const CATEGORY_ALIAS = {
  categoria: ["categoria", "categoría"],
  padre: ["padre"],
  cuenta_ingresos: ["cuenta de ingresos", "cuenta ingresos"],
  cuenta_gasto: ["cuenta de gasto", "cuenta gasto"],
  cuenta_anticipo: ["cuenta de anticipo", "cuenta anticipo"],
}
const PRODUCT_ALIAS = {
  producto: ["producto", "nombre"],
  referencia: ["referencia", "sku"],
  precio_venta: ["precio venta", "precio_venta", "precio"],
  coste: ["coste", "costo"],
  cuenta_ingresos: ["cuenta de ingresos", "cuenta ingresos"],
  cuenta_gasto: ["cuenta de gasto", "cuenta gasto"],
  cuenta_diferencia_precio: ["cuenta de diferencia de precio", "cuenta diferencia precio"],
  categoria: ["categoria", "categoría"],
}
const BOM_ALIAS = {
  product_reference: ["referencia producto", "producto"],
  variant_reference: ["referencia variante", "variante"],
  recipe_reference: ["referencia receta", "receta"],
  quantity: ["cantidad", "cantidad total"],
  component_reference: ["componente", "referencia componente"],
  component_quantity: [
    "cantidad componente",
    "cantidad (2)",
    "cantidad (componente)",
    "cantidad componente (2)",
    "cantidad__2",
  ],
  unit: ["unidad de medida del producto", "unidad de medida", "unidad"],
}
const EMPLOYEE_ALIAS = {
  primer_nombre: ["primer nombre"],
  segundo_nombre: ["segundo nombre"],
  primer_apellido: ["primer apellido"],
  segundo_apellido: ["segundo apellido"],
  work_email: ["correo", "correo laboral", "email laboral", "work email"],
  work_phone: ["telefono laboral", "teléfono laboral"],
  mobile_phone: ["telefono celular", "teléfono celular", "celular"],
  etiquetas: ["etiquetas", "tags"],
  departamento: ["departamento"],
  puesto_trabajo: ["puesto de trabajo", "puesto"],
  igss_registration_date: ["fecha alta igss", "fecha de alta igss"],
  private_street: ["direccion particular", "dirección particular", "direccion", "dirección"],
  private_email: ["correo privado", "email privado"],
  private_phone: ["telefono privado", "teléfono privado"],
  nacionalidad: ["nacionalidad"],
  identification_id: ["dpi", "identificacion", "identificación"],
  igss_afiliacion: ["no de seguridad social", "numero de seguridad social", "seguro social"],
  igss_nit: ["nit"],
  gender: ["genero", "género"],
  birthday: ["fecha de nacimiento"],
  place_of_birth: ["lugar de nacimiento"],
  country_of_birth: ["pais de nacimiento", "país de nacimiento"],
  emergency_contact: ["nombre de contacto", "contacto de emergencia"],
  emergency_phone: ["telefono del contacto", "teléfono del contacto", "telefono contacto"],
  study_field: ["campo de estudio"],
  study_school: ["escuela", "institucion"],
  marital: ["estado civil"],
  children: ["hijos"],
  apellido_casada: ["apellido de casada"],
  igss_codigo_ocupacion: ["codigo de ocupacion", "código de ocupacion", "codigo ocupacion"],
  igss_condicion_laboral: ["condicion laboral"],
  igss_tiempo_contrato: ["tiempo de contrato"],
}

const AccountsUpload = () => {
  const [mode, setMode] = useState(DEFAULT_MODE)
  const [fileName, setFileName] = useState("")
  const [headers, setHeaders] = useState([])
  const [rows, setRows] = useState([])
  const [payload, setPayload] = useState([])
  const [skipped, setSkipped] = useState(0)
  const [error, setError] = useState("")
  const [toast, setToast] = useState(null)
  const [sending, setSending] = useState(false)
  const [successPopup, setSuccessPopup] = useState(false)

  // Categorías
  const [categoryOptions, setCategoryOptions] = useState([])
  const [categoryLoading, setCategoryLoading] = useState(false)
  const [categoryError, setCategoryError] = useState("")
  const [selectedCategories, setSelectedCategories] = useState({})
  const [openCategoryRow, setOpenCategoryRow] = useState(null)

  // Atributos por fila
  const [attributeOptions, setAttributeOptions] = useState([])
  const [attributeLoading, setAttributeLoading] = useState(false)
  const [attributeError, setAttributeError] = useState("")
  const [rowAttributes, setRowAttributes] = useState({})
  const [openAttributeRow, setOpenAttributeRow] = useState(null)

  const fileRef = useRef(null)
  const currentConfig = MODE_CONFIG[mode]

  useEffect(() => {
    if (successPopup) {
      const hideTimer = setTimeout(() => setSuccessPopup(false), 7000)
      return () => clearTimeout(hideTimer)
    }
  }, [successPopup])

  useEffect(() => {
    if (!rows.length) {
      setPayload([])
      setSkipped(0)
      return
    }
    const { valid, skipped: skippedRows } = buildPayload(rows, mode, {
      selectedCategories,
      rowAttributes,
    })
    setPayload(valid)
    setSkipped(skippedRows)
  }, [rows, mode, selectedCategories, rowAttributes])

  const resetInteractiveState = () => {
    setSelectedCategories({})
    setRowAttributes({})
    setOpenCategoryRow(null)
    setOpenAttributeRow(null)
    setCategoryError("")
    setAttributeError("")
  }

  const handleModeChange = (e) => {
    setMode(e.target.value)
    setFileName("")
    setHeaders([])
    setRows([])
    setPayload([])
    setSkipped(0)
    setError("")
    setToast(null)
    resetInteractiveState()
    if (fileRef.current) fileRef.current.value = ""
  }

  const handleFile = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    setError("")
    setToast(null)
    resetInteractiveState()

    const reader = new FileReader()
    reader.onload = () => {
      try {
        const buffer = reader.result
        const { headers: sheetHeaders, rows: sheetRows } = extractRows(buffer)
        const { valid, skipped: skippedRows } = buildPayload(sheetRows, mode, {
          selectedCategories: {},
          rowAttributes: {},
        })
        setHeaders(sheetHeaders)
        setRows(sheetRows)
        setPayload(valid)
        setSkipped(skippedRows)

        if (!sheetHeaders.length || !sheetRows.length) {
          setToast({ ok: false, msg: "No se detectaron filas en la hoja seleccionada." })
        } else if (!valid.length && !skippedRows) {
          setToast({ ok: false, msg: currentConfig.emptyMessage })
        } else if (skippedRows > 0) {
          setToast({ ok: false, msg: currentConfig.skipMessage(skippedRows) })
        }
      } catch (err) {
        console.error(err)
        setError("No se pudo leer el archivo. Verifica que sea un XLSX válido.")
        setHeaders([])
        setRows([])
        setPayload([])
        setSkipped(0)
      }
    }
    reader.onerror = () => setError("Error leyendo el archivo.")
    reader.readAsArrayBuffer(file)
  }

  const handleClear = () => {
    setMode(DEFAULT_MODE)
    setFileName("")
    setHeaders([])
    setRows([])
    setPayload([])
    setSkipped(0)
    setError("")
    setToast(null)
    setSuccessPopup(false)
    resetInteractiveState()
    if (fileRef.current) fileRef.current.value = ""
  }

  const handleSend = async () => {
    if (!payload.length) {
      setToast({ ok: false, msg: currentConfig.emptyMessage })
      return
    }
    setSending(true)
    setToast(null)
    try {
      const res = await fetch(currentConfig.apiUrl, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [currentConfig.collectionKey]: payload }),
      })
      if (!res.ok) throw new Error("Error al enviar.")
      setSuccessPopup(true)
      setToast({ ok: true, msg: `¡Listo! Se enviaron ${payload.length} ${currentConfig.summaryUnit}.` })
    } catch (err) {
      setToast({ ok: false, msg: err.message || "Error al enviar." })
    } finally {
      setSending(false)
    }
  }

  // --- Categorías por fila ---
  const toggleCategoryForRow = async (rowIndex) => {
    const nextRow = openCategoryRow === rowIndex ? null : rowIndex
    setOpenCategoryRow(nextRow)
    if (nextRow !== null && !categoryOptions.length && !categoryLoading) {
      await fetchCategories()
    }
  }

  const fetchCategories = async () => {
    setCategoryLoading(true)
    setCategoryError("")
    try {
      const res = await fetch(CATEGORY_LIST_URL)
      if (!res.ok) throw new Error("No se pudieron obtener las categorías.")
      const data = await res.json()
      setCategoryOptions(data?.data || [])
    } catch (err) {
      setCategoryError(err.message || "Error al obtener categorías.")
    } finally {
      setCategoryLoading(false)
    }
  }

  const handleCategorySelect = (rowIndex, value) => {
    setSelectedCategories((prev) => ({ ...prev, [rowIndex]: value }))
    setOpenCategoryRow(null)
  }

  // --- Atributos por fila ---
  const toggleAttributesForRow = async (rowIndex) => {
    const nextRow = openAttributeRow === rowIndex ? null : rowIndex
    setOpenAttributeRow(nextRow)
    if (nextRow !== null && !attributeOptions.length && !attributeLoading) {
      await fetchAttributes()
    }
    setRowAttributes((prev) => {
      if (nextRow === null || prev[rowIndex]) return prev
      return { ...prev, [rowIndex]: [] }
    })
  }

  const fetchAttributes = async () => {
    setAttributeLoading(true)
    setAttributeError("")
    try {
      const res = await fetch(ATTRIBUTE_LIST_URL)
      if (!res.ok) throw new Error("No se pudieron obtener los atributos.")
      const data = await res.json()
      setAttributeOptions(data?.data || [])
    } catch (err) {
      setAttributeError(err.message || "Error al obtener atributos.")
    } finally {
      setAttributeLoading(false)
    }
  }

  const addAttributeRow = (rowIndex) => {
    setRowAttributes((prev) => {
      const next = { ...prev }
      const current = next[rowIndex] ? [...next[rowIndex]] : []
      current.push({ attributeId: "", valueIds: [] })
      next[rowIndex] = current
      return next
    })
  }

  const removeAttributeRow = (rowIndex, index) => {
    setRowAttributes((prev) => {
      const current = prev[rowIndex] ? [...prev[rowIndex]] : []
      current.splice(index, 1)
      return { ...prev, [rowIndex]: current }
    })
  }

  const handleAttributeChange = (rowIndex, attrIndex, attributeId) => {
    setRowAttributes((prev) => {
      const current = prev[rowIndex] ? [...prev[rowIndex]] : []
      current[attrIndex] = { attributeId, valueIds: [] }
      return { ...prev, [rowIndex]: current }
    })
  }

  const handleAttributeValueToggle = (rowIndex, attrIndex, valueId, checked) => {
    setRowAttributes((prev) => {
      const current = prev[rowIndex] ? [...prev[rowIndex]] : []
      const row = current[attrIndex] || { attributeId: "", valueIds: [] }
      const valueSet = new Set(row.valueIds.map((v) => String(v)))
      if (checked) valueSet.add(String(valueId))
      else valueSet.delete(String(valueId))
      current[attrIndex] = { ...row, valueIds: Array.from(valueSet) }
      return { ...prev, [rowIndex]: current }
    })
  }

  const renderProductCategoryCell = (rowIndex) => {
    const selected = selectedCategories[rowIndex]
    const selectedLabel =
      categoryOptions.find((opt) => String(opt.id) === String(selected))?.name || selected || ""
    return (
      <div className="product-action-cell">
        <button className="btn btn-secondary w-full" onClick={() => toggleCategoryForRow(rowIndex)}>
          {selected ? `Categoría: ${selectedLabel}` : "Añadir categoría"}
        </button>
        {openCategoryRow === rowIndex && (
          <div className="inline-panel">
            {categoryLoading && !categoryOptions.length ? (
              <div className="inline-panel-info">Cargando categorías...</div>
            ) : categoryError ? (
              <div className="inline-panel-info inline-error">{categoryError}</div>
            ) : (
              <select
                className="category-select"
                value={selected || ""}
                onChange={(e) => handleCategorySelect(rowIndex, e.target.value)}
              >
                <option value="">Seleccionar</option>
                {categoryOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderProductAttributesCell = (rowIndex) => {
    const rowsForProduct = rowAttributes[rowIndex] || []
    return (
      <div className="product-action-cell">
        <button className="btn btn-secondary w-full" onClick={() => toggleAttributesForRow(rowIndex)}>
          {rowsForProduct.length ? `${rowsForProduct.length} atributo(s)` : "Añadir atributos"}
        </button>
        {openAttributeRow === rowIndex && (
          <div className="inline-panel">
            {attributeLoading && !attributeOptions.length ? (
              <div className="inline-panel-info">Cargando atributos...</div>
            ) : attributeError ? (
              <div className="inline-panel-info inline-error">{attributeError}</div>
            ) : (
              <>
                <div className="inline-panel-info">Selecciona un atributo y sus valores.</div>
                <div className="attribute-inline-table">
                  <div className="attribute-inline-header">
                    <span>Atributo</span>
                    <span>Valores</span>
                    <span />
                  </div>
                  {rowsForProduct.length === 0 && (
                    <div className="attributes-empty">No hay filas. Usa “Agregar atributo”.</div>
                  )}
                  {rowsForProduct.map((row, attrIndex) => {
                    const attribute = attributeOptions.find((opt) => String(opt.id) === String(row.attributeId))
                    const valueOptions = attribute?.values || []
                    return (
                      <div className="attributes-row" key={attrIndex}>
                        <select
                          className="attribute-select"
                          value={row.attributeId}
                          onChange={(e) => handleAttributeChange(rowIndex, attrIndex, e.target.value)}
                        >
                          <option value="">Seleccionar</option>
                          {attributeOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name}
                            </option>
                          ))}
                        </select>
                        {(!row.attributeId || !valueOptions.length) && (
                          <div className="attribute-values-column disabled">Selecciona un atributo con valores.</div>
                        )}
                        {row.attributeId && valueOptions.length > 0 && (
                          <div className="attribute-values-column">
                            <div
                              className={`attribute-values-summary ${
                                row.valueIds.length ? "" : "empty"
                              }`}
                            >
                              {row.valueIds.length
                                ? row.valueIds
                                    .map(
                                      (valId) =>
                                        valueOptions.find((val) => String(val.id) === String(valId))?.name,
                                    )
                                    .filter(Boolean)
                                    .join(", ")
                                : "Selecciona uno o más valores"}
                            </div>
                            <div className="attribute-values-list">
                              {valueOptions.map((val) => {
                                const checked = row.valueIds.includes(String(val.id))
                                return (
                                  <label key={val.id} className="attribute-value-option">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) =>
                                        handleAttributeValueToggle(rowIndex, attrIndex, val.id, e.target.checked)
                                      }
                                    />
                                    <span>{val.name}</span>
                                  </label>
                                )
                              })}
                            </div>
                          </div>
                        )}
                        <button className="btn btn-ghost attribute-remove" onClick={() => removeAttributeRow(rowIndex, attrIndex)}>
                          Eliminar
                        </button>
                      </div>
                    )
                  })}
                </div>
                <div className="attribute-inline-actions">
                  <button className="btn btn-secondary" onClick={() => addAttributeRow(rowIndex)}>
                    Agregar atributo
                  </button>
                  <button className="btn btn-primary" onClick={() => setOpenAttributeRow(null)}>
                    Confirmar
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderTableHead = () => {
    const productMode = mode === "productos"
    return (
      <tr>
        {headers.map((h, i) => (
          <th key={i}>{h}</th>
        ))}
        {productMode && (
          <>
            <th>Categoría</th>
            <th>Atributos</th>
          </>
        )}
      </tr>
    )
  }

  const renderTableBody = () => {
    const productMode = mode === "productos"
    return rows.map((row, rowIndex) => (
      <tr key={rowIndex}>
        {headers.map((h, colIndex) => (
          <td key={colIndex}>{row[h] || ""}</td>
        ))}
        {productMode && (
          <>
            <td>{renderProductCategoryCell(rowIndex)}</td>
            <td>{renderProductAttributesCell(rowIndex)}</td>
          </>
        )}
      </tr>
    ))
  }

  return (
    <div className="crm-container">
      {successPopup && (
        <div className="success-popup">
          <div className="success-popup-content">
            <div className="success-icon">✓</div>
            <div className="success-text">Envío exitoso</div>
          </div>
        </div>
      )}

      <header className="hero">
        <div className="hero-title">{currentConfig.heroTitle}</div>
        <div className="hero-subtitle">XLSX → Vista previa → Envío</div>
      </header>

      <section className="card">
        <div className="mode-switch">
          <label className="mode-label" htmlFor="modeSelect">
            Tipo de carga
          </label>
          <select id="modeSelect" value={mode} onChange={handleModeChange} className="mode-select">
            {MODE_ORDER.map((value) => (
              <option key={value} value={value}>
                {MODE_CONFIG[value].label}
              </option>
            ))}
          </select>
        </div>

        <div className="uploader">
          <label className="file-label" htmlFor="xlsxInput">
            <span className="file-label-title">Seleccionar XLSX</span>
            <small className="file-label-note">{currentConfig.fileNote}</small>
          </label>
          <div className="file-input-wrapper">
            <input
              id="xlsxInput"
              ref={fileRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFile}
            />
            <div className="file-input-display">
              <div className="file-input-button">Buscar</div>
              <div className="file-input-text">{fileName || "seleccionar archivo"}</div>
            </div>
          </div>
        </div>

        <div className="summary">
          <div className={`badge ${rows.length ? "" : "badge-empty"}`}>
            {rows.length ? `${rows.length} fila(s) detectadas` : "Sin datos"}
          </div>
          {payload.length > 0 && (
            <div className="hint">
              <span>Listas para enviar:</span> {payload.length} {currentConfig.summaryUnit}
            </div>
          )}
        </div>

        {categoryError && <div className="alert alert-error">{categoryError}</div>}
        {attributeError && <div className="alert alert-error">{attributeError}</div>}
        {skipped > 0 && <div className="alert alert-error">{currentConfig.skipMessage(skipped)}</div>}
        {error && <div className="alert alert-error">{error}</div>}
        {toast && <div className={`alert ${toast.ok ? "alert-success" : "alert-error"}`}>{toast.msg}</div>}

        {rows.length > 0 && (
          <>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>{renderTableHead()}</thead>
                <tbody>{renderTableBody()}</tbody>
              </table>
            </div>

            <div className="actions">
              <button className="btn btn-primary" onClick={handleSend} disabled={sending || !payload.length}>
                {sending ? "Enviando..." : "Enviar"}
              </button>
              <button className="btn btn-ghost" onClick={handleClear}>
                Limpiar
              </button>
            </div>
          </>
        )}
      </section>

      <footer className="footnote">{currentConfig.footnote}</footer>
    </div>
  )
}

export default AccountsUpload

function extractRows(buffer) {
  if (!buffer) return { headers: [], rows: [] }
  const workbook = read(buffer, { type: "array" })
  const sheet = workbook.Sheets[workbook.SheetNames[0]]
  if (!sheet) return { headers: [], rows: [] }
  const matrix = utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    raw: false,
    cellDates: true,
    dateNF: "dd/mm/yyyy",
  })
  if (!matrix.length) return { headers: [], rows: [] }
  const rawHeaders = (matrix.shift() || []).map((h) => String(h || "").trim())
  const headers = makeUniqueHeaders(rawHeaders)
  const rows = matrix
    .map((cells) => {
      const obj = {}
      headers.forEach((header, idx) => {
        obj[header] = cells[idx] !== undefined ? String(cells[idx]).trim() : ""
      })
      return obj
    })
    .filter((row) => Object.values(row).some((value) => String(value).trim() !== ""))
  return { headers, rows }
}

function buildPayload(rows, mode, extras = {}) {
  if (mode === "categorias") return buildCategoriesPayload(rows)
  if (mode === "productos") return buildProductsPayload(rows, extras)
  if (mode === "boms") return buildBomsPayload(rows)
  if (mode === "empleados") return buildEmployeesPayload(rows)
  return buildAccountsPayload(rows)
}

function buildAccountsPayload(rows) {
  const valid = []
  let skipped = 0
  rows.forEach((row) => {
    const codigo = getFieldByAliases(row, ACCOUNT_ALIAS.codigo)
    const nombre = getFieldByAliases(row, ACCOUNT_ALIAS.nombre)
    const moneda = getFieldByAliases(row, ACCOUNT_ALIAS.moneda)
    if (!codigo && !nombre && !moneda) return
    if (codigo && nombre && moneda) {
      valid.push({ codigo, nombre, moneda: moneda.toUpperCase() })
    } else {
      skipped++
    }
  })
  return { valid, skipped }
}

function buildCategoriesPayload(rows) {
  const valid = []
  let skipped = 0
  rows.forEach((row) => {
    const categoria = getFieldByAliases(row, CATEGORY_ALIAS.categoria)
    const padre = getFieldByAliases(row, CATEGORY_ALIAS.padre)
    const cuentaIngresos = getFieldByAliases(row, CATEGORY_ALIAS.cuenta_ingresos)
    const cuentaGasto = getFieldByAliases(row, CATEGORY_ALIAS.cuenta_gasto)
    const cuentaAnticipo = getFieldByAliases(row, CATEGORY_ALIAS.cuenta_anticipo)
    if (!categoria && !padre && !cuentaIngresos && !cuentaGasto && !cuentaAnticipo) return
    if (categoria && cuentaIngresos && cuentaGasto && cuentaAnticipo) {
      valid.push({
        categoria,
        padre,
        cuenta_ingresos: cuentaIngresos,
        cuenta_gasto: cuentaGasto,
        cuenta_anticipo: cuentaAnticipo,
      })
    } else {
      skipped++
    }
  })
  return { valid, skipped }
}

function buildProductsPayload(rows, extras) {
  const selectedCategories = extras.selectedCategories || {}
  const rowAttributes = extras.rowAttributes || {}
  const valid = []
  let skipped = 0
  rows.forEach((row, index) => {
    const producto = getFieldByAliases(row, PRODUCT_ALIAS.producto)
    const referencia = getFieldByAliases(row, PRODUCT_ALIAS.referencia)
    const precioVenta = getFieldByAliases(row, PRODUCT_ALIAS.precio_venta)
    const coste = getFieldByAliases(row, PRODUCT_ALIAS.coste)
    const cuentaIngresos = getFieldByAliases(row, PRODUCT_ALIAS.cuenta_ingresos)
    const cuentaGasto = getFieldByAliases(row, PRODUCT_ALIAS.cuenta_gasto)
    const cuentaDiferencia = getFieldByAliases(row, PRODUCT_ALIAS.cuenta_diferencia_precio)
    const categoriaSheet = getFieldByAliases(row, PRODUCT_ALIAS.categoria)
    const categoriaSeleccionada = selectedCategories[index] || categoriaSheet

    if (
      !producto &&
      !referencia &&
      !precioVenta &&
      !coste &&
      !cuentaIngresos &&
      !cuentaGasto &&
      !cuentaDiferencia &&
      !categoriaSeleccionada
    ) {
      return
    }

    if (
      producto &&
      referencia &&
      precioVenta &&
      coste &&
      cuentaIngresos &&
      cuentaGasto &&
      cuentaDiferencia &&
      categoriaSeleccionada
    ) {
      const item = {
        producto,
        referencia,
        precio_venta: toNumber(precioVenta),
        coste: toNumber(coste),
        cuenta_ingresos: cuentaIngresos,
        cuenta_gasto: cuentaGasto,
        cuenta_diferencia_precio: cuentaDiferencia,
        categoria: Number(categoriaSeleccionada) || categoriaSeleccionada,
      }
      const attrs = rowAttributes[index] || []
      attrs
        .filter((attr) => attr.attributeId && attr.valueIds && attr.valueIds.length)
        .forEach((attr, idx) => {
          item[`atributo${idx + 1}`] = Number(attr.attributeId) || attr.attributeId
          item[`valores${idx + 1}`] = attr.valueIds.join(",")
        })
      valid.push(item)
    } else {
      skipped++
    }
  })
  return { valid, skipped }
}

function buildBomsPayload(rows) {
  const map = new Map()
  let skipped = 0
  let lastProduct = ""
  let lastVariant = ""
  let lastRecipe = ""
  let lastQuantity = ""

  rows.forEach((row) => {
    const productRaw = getFieldByAliases(row, BOM_ALIAS.product_reference)
    const variantRaw = getFieldByAliases(row, BOM_ALIAS.variant_reference)
    const recipeRaw = getFieldByAliases(row, BOM_ALIAS.recipe_reference)
    const quantityRaw = getFieldByAliases(row, BOM_ALIAS.quantity)

    if (productRaw) lastProduct = productRaw
    if (variantRaw) lastVariant = variantRaw
    if (recipeRaw) lastRecipe = recipeRaw
    if (quantityRaw) lastQuantity = quantityRaw

    const product = productRaw || lastProduct
    const variant = variantRaw || lastVariant
    const recipe = recipeRaw || lastRecipe
    const quantity = quantityRaw || lastQuantity

    if (!product || !variant || !recipe) {
      skipped++
      return
    }

    const componentRef = getFieldByAliases(row, BOM_ALIAS.component_reference)
    const componentQtyRaw = getFieldByAliases(row, BOM_ALIAS.component_quantity)
    const unit = getFieldByAliases(row, BOM_ALIAS.unit)

    if (!componentRef || !componentQtyRaw || !unit) {
      skipped++
      return
    }

    const key = `${product}|${variant}|${recipe}`
    if (!map.has(key)) {
      map.set(key, {
        product_reference: product,
        variant_reference: variant,
        referencia_receta: recipe,
        cantidad: toNumber(quantity) || 0,
        componentes: [],
      })
    } else if (quantity && !map.get(key).cantidad) {
      map.get(key).cantidad = toNumber(quantity) || 0
    }

    map.get(key).componentes.push({
      referencia: componentRef,
      cantidad: toNumber(componentQtyRaw) || 0,
      unidad_medida: unit,
    })
  })

  const valid = Array.from(map.values()).filter((bom) => bom.componentes.length)
  return { valid, skipped }
}

function buildEmployeesPayload(rows) {
  const valid = []
  let skipped = 0
  rows.forEach((row) => {
    const primerNombre = getFieldByAliases(row, EMPLOYEE_ALIAS.primer_nombre)
    const primerApellido = getFieldByAliases(row, EMPLOYEE_ALIAS.primer_apellido)
    const correoTrabajo = getFieldByAliases(row, EMPLOYEE_ALIAS.work_email)

    const hasData = Object.values(row).some((value) => String(value).trim() !== "")
    if (!hasData) return

    if (!primerNombre || !primerApellido || !correoTrabajo) {
      skipped++
      return
    }

    const empleado = {
      primer_nombre: primerNombre,
      segundo_nombre: getFieldByAliases(row, EMPLOYEE_ALIAS.segundo_nombre),
      primer_apellido: primerApellido,
      segundo_apellido: getFieldByAliases(row, EMPLOYEE_ALIAS.segundo_apellido),
      work_email: correoTrabajo,
      work_phone: getFieldByAliases(row, EMPLOYEE_ALIAS.work_phone),
      mobile_phone: getFieldByAliases(row, EMPLOYEE_ALIAS.mobile_phone),
      etiquetas: splitTags(getFieldByAliases(row, EMPLOYEE_ALIAS.etiquetas)),
      departamento: getFieldByAliases(row, EMPLOYEE_ALIAS.departamento),
      puesto_trabajo: getFieldByAliases(row, EMPLOYEE_ALIAS.puesto_trabajo),
      igss_registration_date: normalizeDate(getFieldByAliases(row, EMPLOYEE_ALIAS.igss_registration_date)),
      private_street: getFieldByAliases(row, EMPLOYEE_ALIAS.private_street),
      private_email: getFieldByAliases(row, EMPLOYEE_ALIAS.private_email),
      private_phone: getFieldByAliases(row, EMPLOYEE_ALIAS.private_phone),
      nacionalidad: getFieldByAliases(row, EMPLOYEE_ALIAS.nacionalidad),
      identification_id: getFieldByAliases(row, EMPLOYEE_ALIAS.identification_id),
      igss_afiliacion: getFieldByAliases(row, EMPLOYEE_ALIAS.igss_afiliacion),
      igss_nit: getFieldByAliases(row, EMPLOYEE_ALIAS.igss_nit),
      gender: getFieldByAliases(row, EMPLOYEE_ALIAS.gender),
      birthday: normalizeDate(getFieldByAliases(row, EMPLOYEE_ALIAS.birthday)),
      place_of_birth: getFieldByAliases(row, EMPLOYEE_ALIAS.place_of_birth),
      country_of_birth: getFieldByAliases(row, EMPLOYEE_ALIAS.country_of_birth),
      emergency_contact: getFieldByAliases(row, EMPLOYEE_ALIAS.emergency_contact),
      emergency_phone: getFieldByAliases(row, EMPLOYEE_ALIAS.emergency_phone),
      study_field: getFieldByAliases(row, EMPLOYEE_ALIAS.study_field),
      study_school: getFieldByAliases(row, EMPLOYEE_ALIAS.study_school),
      marital: getFieldByAliases(row, EMPLOYEE_ALIAS.marital),
      children: numericOrNull(getFieldByAliases(row, EMPLOYEE_ALIAS.children)),
      apellido_casada: getFieldByAliases(row, EMPLOYEE_ALIAS.apellido_casada),
      igss_codigo_ocupacion: getFieldByAliases(row, EMPLOYEE_ALIAS.igss_codigo_ocupacion),
      igss_condicion_laboral: getFieldByAliases(row, EMPLOYEE_ALIAS.igss_condicion_laboral),
      igss_tiempo_contrato: getFieldByAliases(row, EMPLOYEE_ALIAS.igss_tiempo_contrato),
    }
    valid.push(empleado)
  })
  return { valid, skipped }
}

function getFieldByAliases(row, aliases = []) {
  const normalized = {}
  Object.keys(row).forEach((key) => {
    normalized[normKey(key)] = row[key]
  })
  for (const alias of aliases) {
    const value = normalized[normKey(alias)]
    if (value !== undefined && String(value).trim() !== "") {
      return String(value).trim()
    }
  }
  return ""
}

function normKey(value = "") {
  return removeAccents(String(value).toLowerCase().trim())
}

function removeAccents(str = "") {
  try {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  } catch {
    return str
  }
}

function toNumber(value) {
  const normalized = String(value).replace(/,/g, ".")
  const match = normalized.match(/-?\d+(\.\d+)?/)
  if (!match) return 0
  const parsed = Number(match[0])
  return Number.isFinite(parsed) ? parsed : 0
}

function makeUniqueHeaders(list) {
  const seen = {}
  return list.map((header, idx) => {
    const base = header || `Columna ${idx + 1}`
    const key = normKey(base) || `columna${idx + 1}`
    const count = seen[key] || 0
    seen[key] = count + 1
    if (count === 0) return base
    return `${base || "Columna"} (${count + 1})`
  })
}

function splitTags(raw) {
  if (!raw) return []
  return Array.from(
    new Set(
      String(raw)
        .split(/[;,|]/g)
        .map((tag) => tag.trim())
        .filter(Boolean),
    ),
  )
}

function normalizeDate(raw) {
  if (!raw) return ""
  const trimmed = String(raw).trim()
  if (!trimmed) return ""
  const slashMatch = trimmed.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/)
  if (slashMatch) {
    let [, d, m, y] = slashMatch
    if (y.length === 2) {
      const yr = Number(y)
      const currentTwoDigits = Number(String(new Date().getFullYear()).slice(-2))
      y = yr > currentTwoDigits ? `19${y}` : `20${y.padStart(2, "0")}`
    }
    return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`
  }
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return trimmed
  const day = String(parsed.getDate()).padStart(2, "0")
  const month = String(parsed.getMonth() + 1).padStart(2, "0")
  const year = String(parsed.getFullYear())
  return `${day}/${month}/${year}`
}

function numericOrNull(value) {
  if (value === undefined || value === null) return null
  const num = Number(String(value).replace(/[^\d.-]/g, ""))
  if (Number.isFinite(num)) return num
  return null
}
